import { useEffect, useReducer } from 'react'
import { api } from '../Services/api'
import { KEYS, readJSON, removeKey, writeJSON } from '../Utils/storage'
import { useResource } from './refData'

/* Comments: on only when the server says so (GET /config; on by default,
   DISCUSSIONS=0 in the pres repo's api/.env turns them off). Until it
   answers, and on any error, the site looks exactly as without them. */
export function useDiscussions() {
  const { data, error } = useResource('config', () => api.config(), { ttl: 10 * 60000 })
  return {
    enabled: data?.discussions === true,
    // Login (api/login.py, FIREBASE_PROJECT_ID): when on, writing needs it.
    login: data?.login === true,
    loaded: data !== undefined || Boolean(error),
  }
}

/* The anonymous profile a reader comments with: created on the first
   comment or report, kept in this browser. Losing it only means older
   comments no longer show "Obriši". */
let creating = null

export function commentToken() {
  return readJSON(KEYS.commentToken, null)
}

export async function ensureCommentToken({ renew = false } = {}) {
  if (renew) removeKey(KEYS.commentToken)
  const token = commentToken()
  if (token) return token
  if (!creating) {
    creating = api
      .createProfile()
      .then((value) => {
        writeJSON(KEYS.commentToken, value)
        return value
      })
      .finally(() => {
        creating = null
      })
  }
  return creating
}

/* Runs fn(token); an unknown token (profile deleted, logged out elsewhere)
   gets one new anonymous profile. */
export async function withCommentToken(fn) {
  try {
    return await fn(await ensureCommentToken())
  } catch (error) {
    if (error?.response?.status !== 401) throw error
    setAccount(null)
    return fn(await ensureCommentToken({ renew: true }))
  }
}

/* The logged-in account ({ email, name, provider }) or null. The session is
   the profile token above; this is what the page shows about it. */
let account = readJSON(KEYS.account, null)
const accountListeners = new Set()

function setAccount(value) {
  account = value
  if (value) writeJSON(KEYS.account, value)
  else removeKey(KEYS.account)
  accountListeners.forEach((fn) => fn())
}

export function useAccount() {
  const [, rerender] = useReducer((n) => n + 1, 0)
  useEffect(() => {
    accountListeners.add(rerender)
    return () => accountListeners.delete(rerender)
  }, [])
  return account
}

/* A Firebase ID token -> logged in: this browser's anonymous profile (votes,
   reports) joins the account and the API's token replaces it. */
export async function finishLogin(idToken) {
  const r = await api.login(idToken, commentToken())
  writeJSON(KEYS.commentToken, r.token)
  const { email, name, provider } = r.account
  setAccount({ email, name, provider })
  if (name) saveCommentName(name)
  return r.account
}

export async function setAccountName(name) {
  const r = await api.setAccountName(name, commentToken())
  setAccount({ ...account, name: r.name })
  saveCommentName(r.name)
}

/* This browser's token stops working; the next comment, like or report
   starts a new anonymous profile. */
export async function logout() {
  const token = commentToken()
  try {
    if (token) await api.logout(token)
  } catch (e) {
    // gone already: forget it anyway
  }
  removeKey(KEYS.commentToken)
  removeKey(KEYS.commentName)
  forgetMyVotes()
  setAccount(null)
}

/* The server said this profile isn't logged in (403): show the login again. */
export const accountExpired = () => setAccount(null)

export const savedCommentName = () => readJSON(KEYS.commentName, '')
export const saveCommentName = (name) => writeJSON(KEYS.commentName, name)

/* Likes and dislikes on comments, shared by every place a comment shows (the
   Glavna vijest's best comment and the comments panel), so a vote in one shows
   in the other. Cards are cached for everyone, so my own votes are kept in
   this browser; the server counts one vote per profile. */
const MAX_VOTES = 500
const myVotes = new Map(Object.entries(readJSON(KEYS.commentVotes, {})).map(([k, v]) => [Number(k), v]))
const overrides = new Map() // comment id -> { vote, likes, dislikes } after a tap here
const listeners = new Set()

function forgetMyVotes() {
  myVotes.clear()
  overrides.clear()
  removeKey(KEYS.commentVotes)
  listeners.forEach((fn) => fn())
}

function setVote(id, value) {
  overrides.set(id, value)
  myVotes.delete(id)
  if (value.vote) myVotes.set(id, value.vote)
  writeJSON(KEYS.commentVotes, Object.fromEntries([...myVotes].slice(-MAX_VOTES)))
  listeners.forEach((fn) => fn())
}

/* { vote: 1 | -1 | 0, likes, dislikes, cast(value) }: casting the vote you
   already have takes it back. */
export function useCommentVote(comment) {
  const [, rerender] = useReducer((n) => n + 1, 0)
  useEffect(() => {
    listeners.add(rerender)
    return () => listeners.delete(rerender)
  }, [])
  const state = overrides.get(comment.id) || {
    vote: comment.vote || myVotes.get(comment.id) || 0,
    likes: comment.likes || 0,
    dislikes: comment.dislikes || 0,
  }
  const cast = async (value) => {
    const vote = state.vote === value ? 0 : value
    setVote(comment.id, {
      vote,
      likes: state.likes - (state.vote === 1) + (vote === 1),
      dislikes: state.dislikes - (state.vote === -1) + (vote === -1),
    })
    try {
      const r = await withCommentToken((token) => api.voteComment(comment.id, vote, token))
      setVote(comment.id, { vote: r.vote, likes: r.likes, dislikes: r.dislikes })
    } catch (error) {
      setVote(comment.id, state)
      throw error
    }
  }
  return { ...state, cast }
}
